/**
 * Message Type Codes (Enum)
 */
export enum MessageTypeCode {
  SUMMARY_FULL_PAGE = 'SUMMARY_FULL_PAGE',
  FUND_SPECIFIC_FULL_PAGE = 'FUND_SPECIFIC_FULL_PAGE',
  FUND_HALF_PAGE = 'FUND_HALF_PAGE',
  MARKET_NEWS_FULL_PAGE = 'MARKET_NEWS_FULL_PAGE',
  FUND_HARD_CODED_FULL_PAGE = 'FUND_HARD_CODED_FULL_PAGE',
  HALF_PAGE_85_CHARS_3_LINE = 'HALF_PAGE_85_CHARS_3_LINE',
  HALF_PAGE_85_CHARS_9_LINE = 'HALF_PAGE_85_CHARS_9_LINE',
  HALF_PAGE_85_CHARS_20_LINE = 'HALF_PAGE_85_CHARS_20_LINE',
  FULL_PAGE_155_CHARS_5_LINE = 'FULL_PAGE_155_CHARS_5_LINE',
  THIRD_PAGE_55_CHARS_3_LINE = 'THIRD_PAGE_55_CHARS_3_LINE',
  TWO_THIRDS_PAGE_100_CHARS_4_LINE = 'TWO_THIRDS_PAGE_100_CHARS_4_LINE',
  FULL_PAGE_155_CHARS_9_LINE = 'FULL_PAGE_155_CHARS_9_LINE',
  HALF_PAGE_70_CHARS_13_LINE = 'HALF_PAGE_70_CHARS_13_LINE',
  FULL_PAGE_155_CHARS_20_LINE = 'FULL_PAGE_155_CHARS_20_LINE',
}

/**
 * Message Type Metadata Interface
 */
export interface MessageTypeMetadata {
  key: MessageTypeCode;
  value: string;
  code: string;
}

/**
 * Text wrapping result interface
 */
export interface TextWrappingResult {
  lines: string[];
  isValid: boolean;
  errors: string[];
  stats: {
    totalLines: number;
    maxLines: number;
    maxCharsPerLine: number;
    longestLine: number;
  };
}

/**
 * Message limits interface
 */
export interface MessageLimits {
  maxChars: number;
  maxLines: number;
}

/**
 * Message Types with Code and Text
 */
export const MESSAGE_TYPES: Record<MessageTypeCode, MessageTypeMetadata> = {
  [MessageTypeCode.SUMMARY_FULL_PAGE]: {
    key: MessageTypeCode.SUMMARY_FULL_PAGE,
    value: 'Summary full page 110 chars x 12 line',
    code: '01',
  },
  [MessageTypeCode.FUND_SPECIFIC_FULL_PAGE]: {
    key: MessageTypeCode.FUND_SPECIFIC_FULL_PAGE,
    value: 'Fund specific full page 115 chars x 5 line',
    code: '02',
  },
  [MessageTypeCode.FUND_HALF_PAGE]: {
    key: MessageTypeCode.FUND_HALF_PAGE,
    value: 'Fund 1/2 page 60 chars x 12 line',
    code: '03',
  },
  [MessageTypeCode.MARKET_NEWS_FULL_PAGE]: {
    key: MessageTypeCode.MARKET_NEWS_FULL_PAGE,
    value: 'Market News full page 115 chars x 20 line',
    code: '04',
  },
  [MessageTypeCode.FUND_HARD_CODED_FULL_PAGE]: {
    key: MessageTypeCode.FUND_HARD_CODED_FULL_PAGE,
    value: 'Fund hard-coded full page 115 chars x 5 line',
    code: '05',
  },
  [MessageTypeCode.HALF_PAGE_85_CHARS_3_LINE]: {
    key: MessageTypeCode.HALF_PAGE_85_CHARS_3_LINE,
    value: 'Half page 85 chars x 3 line',
    code: '10',
  },
  [MessageTypeCode.HALF_PAGE_85_CHARS_9_LINE]: {
    key: MessageTypeCode.HALF_PAGE_85_CHARS_9_LINE,
    value: 'Half page 85 chars x 9 line',
    code: '11',
  },
  [MessageTypeCode.HALF_PAGE_85_CHARS_20_LINE]: {
    key: MessageTypeCode.HALF_PAGE_85_CHARS_20_LINE,
    value: 'Half page 85 chars x 20 line',
    code: '12',
  },
  [MessageTypeCode.FULL_PAGE_155_CHARS_5_LINE]: {
    key: MessageTypeCode.FULL_PAGE_155_CHARS_5_LINE,
    value: 'Full page 155 chars x 5 line',
    code: '13',
  },
  [MessageTypeCode.THIRD_PAGE_55_CHARS_3_LINE]: {
    key: MessageTypeCode.THIRD_PAGE_55_CHARS_3_LINE,
    value: 'Third page 55 chars x 3 line',
    code: '14',
  },
  [MessageTypeCode.TWO_THIRDS_PAGE_100_CHARS_4_LINE]: {
    key: MessageTypeCode.TWO_THIRDS_PAGE_100_CHARS_4_LINE,
    value: 'Two-thirds page 100 chars x 4 line',
    code: '15',
  },
  [MessageTypeCode.FULL_PAGE_155_CHARS_9_LINE]: {
    key: MessageTypeCode.FULL_PAGE_155_CHARS_9_LINE,
    value: 'Full page 155 chars x 9 line',
    code: '16',
  },
  [MessageTypeCode.HALF_PAGE_70_CHARS_13_LINE]: {
    key: MessageTypeCode.HALF_PAGE_70_CHARS_13_LINE,
    value: 'Half page 70 chars x 13 line',
    code: '17',
  },
  [MessageTypeCode.FULL_PAGE_155_CHARS_20_LINE]: {
    key: MessageTypeCode.FULL_PAGE_155_CHARS_20_LINE,
    value: 'Full page 155 chars x 20 line',
    code: '18',
  },
};

/**
 * Helper: Get array of message type values (descriptions) only
 * @returns Array of string descriptions
 * @example
 * const descriptions = getMessageTypeValues();
 * // ['Summary full page 110 chars x 12 line', 'Fund specific full page...', ...]
 */
export function getMessageTypeValues(): string[] {
  return Object.values(MESSAGE_TYPES).map((type) => type.value);
}

/**
 * Get message type code by value (description)
 * @param value - The message type description (e.g., 'Summary full page 110 chars x 12 line')
 * @returns The numeric code (e.g., '01') or empty string if not found
 * @example
 * const code = getMessageCodeByValue('Summary full page 110 chars x 12 line');
 * // Returns: '01'
 *
 * const code2 = getMessageCodeByValue('Half page 85 chars x 3 line');
 * // Returns: '10'
 */
export function getMessageCodeByValue(value: string): string {
  const messageType = Object.values(MESSAGE_TYPES).find((type) => type.value === value);
  return messageType?.code || '';
}

/**
 * Get message type value (description) by code
 * @param code - The message type code (e.g., '01', '10')
 * @returns The description (e.g., 'Summary full page 110 chars x 12 line') or empty string if not found
 * @example
 * const description = getMessageValueByCode('01');
 * // Returns: 'Summary full page 110 chars x 12 line'
 *
 * const description2 = getMessageValueByCode('10');
 * // Returns: 'Half page 85 chars x 3 line'
 *
 * const description3 = getMessageValueByCode('99');
 * // Returns: '' (not found)
 */
export function getMessageValueByCode(code: string): string {
  const messageType = Object.values(MESSAGE_TYPES).find((type) => type.code === code);
  return messageType?.value || '';
}


/**
 *  ASYNC VERSION: Get message type code by key (enum)
 * @param key - The message type key (e.g., 'SUMMARY_FULL_PAGE', 'FUND_SPECIFIC_FULL_PAGE')
 * @returns Promise with the numeric code (e.g., '01', '02') or empty string if not found
 * @example
 * const code = await getMessageCodeByKeyAsync('SUMMARY_FULL_PAGE');
 * // Returns: '01'
 *
 * const code2 = await getMessageCodeByKeyAsync(MessageTypeCode.FUND_SPECIFIC_FULL_PAGE);
 * // Returns: '02'
 */
export async function getMessageCodeByKeyAsync(key: string | MessageTypeCode): Promise<string> {
  return Promise.resolve(MESSAGE_TYPES[key as MessageTypeCode]?.code || '');
}

/**
 * SYNC VERSION: Get message type code by key (enum) - for immediate use
 * @param key - The message type key
 * @returns The numeric code or empty string if not found
 */
export function getMessageCodeByKey(key: string | MessageTypeCode): string {
  return MESSAGE_TYPES[key as MessageTypeCode]?.code || '';
}

// ============================================
// NEW FUNCTIONS - MESSAGE LIMITS
// ============================================

/**
 * Get character and line limits by message type code
 * @param code - Message type code (e.g., '05' for FUND_HARD_CODED_FULL_PAGE)
 * @returns Object with maxChars and maxLines, or null if not found
 * @example
 * const limits = getMessageLimits('05');
 * // Returns: { maxChars: 115, maxLines: 5 }
 */
export function getMessageLimits(code: string): MessageLimits | null {
  const messageType = Object.values(MESSAGE_TYPES).find((type) => type.code === code);
  if (!messageType) return null;

  // Parse from value string (e.g., "Fund hard-coded full page 115 chars x 5 line")
  const match = /(\d+)\s+chars\s+x\s+(\d+)\s+line/.exec(messageType.value);
  //const match = messageType.value.match(/(\d+)\s+chars\s+x\s+(\d+)\s+line/);
  if (!match) return null;

  return {
    maxChars: parseInt(match[1], 10),
    maxLines: parseInt(match[2], 10),
  };
}

/**
 * Get message limits by message type key (enum)
 * @param key - Message type key (e.g., MessageTypeCode.FUND_HARD_CODED_FULL_PAGE)
 * @returns Object with maxChars and maxLines, or null if not found
 * @example
 * const limits = getMessageLimitsByKey(MessageTypeCode.FUND_HARD_CODED_FULL_PAGE);
 * // Returns: { maxChars: 115, maxLines: 5 }
 */
export function getMessageLimitsByKey(key: MessageTypeCode): MessageLimits | null {
  const code = MESSAGE_TYPES[key]?.code;
  return code ? getMessageLimits(code) : null;
}

// ============================================
// TEXT WRAPPING FUNCTIONS
// ============================================

/**
 * Wrap text intelligently respecting word boundaries
 * @param text - Text to wrap
 * @param maxCharsPerLine - Maximum characters per line
 * @param maxLines - Maximum number of lines allowed
 * @returns TextWrappingResult with wrapped lines and validation info
 * @example
 * const result = wrapText('This is a long body of text that needs wrapping', 115, 5);
 * // Returns: { lines: [...], isValid: true, errors: [], stats: {...} }
 */
// eslint-disable-next-line complexity,max-statements
export function wrapText(text: string, maxCharsPerLine: number, maxLines: number): TextWrappingResult {
  const errors: string[] = [];
  const lines: string[] = [];

  // Handle empty text
  if (!text || text.length === 0) {
    return {
      lines: [],
      isValid: true,
      errors: [],
      stats: {
        totalLines: 0,
        maxLines,
        maxCharsPerLine,
        longestLine: 0,
      },
    };
  }

  const normalizedText = text;

  // Split into words
  const words = normalizedText.split(' ');

  let currentLine = '';
  let longestLine = 0;

  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine ? `${currentLine} ${word}` : word;

    // Check if word itself is too long
    if (word.length > maxCharsPerLine) {
      errors.push(
        `Word "${word}" (${word.length} chars) exceeds maximum line length of ${maxCharsPerLine} chars`,
      );

      // If current line has content, save it first
      if (currentLine) {
        lines.push(currentLine);
        longestLine = Math.max(longestLine, currentLine.length);
      }

      // Add the long word as its own line (it will exceed limit but we track the error)
      lines.push(word);
      longestLine = Math.max(longestLine, word.length);
      currentLine = '';
      continue;
    }

    // Check if adding this word would exceed line length
    if (testLine.length <= maxCharsPerLine) {
      // Word fits on current line
      currentLine = testLine;
    } else {
      // Word doesn't fit, move to next line
      if (currentLine) {
        lines.push(currentLine);
        longestLine = Math.max(longestLine, currentLine.length);
      }
      currentLine = word;
    }
  }

  // Add the last line if it has content
  if (currentLine) {
    lines.push(currentLine);
    longestLine = Math.max(longestLine, currentLine.length);
  }

  // Validate line count
  const isValid = lines.length <= maxLines && errors.length === 0;

  if (lines.length > maxLines) {
    errors.push(
      `Text requires ${lines.length} lines but maximum allowed is ${maxLines} lines`,
    );
  }

  return {
    lines,
    isValid,
    errors,
    stats: {
      totalLines: lines.length,
      maxLines,
      maxCharsPerLine,
      longestLine,
    },
  };
}

/**
 * Wrap text for specific message type using its code
 * @param text - Text to wrap
 * @param messageTypeCode - Message type code (e.g., '05')
 * @returns TextWrappingResult or null if message type not found
 * @example
 * const result = wrapTextForMessageType('Long text here', '05');
 * // Automatically uses 115 chars x 5 lines limits for FUND_HARD_CODED_FULL_PAGE
 */
export function wrapTextForMessageType(
  text: string,
  messageTypeCode: string,
): TextWrappingResult | null {
  const limits = getMessageLimits(messageTypeCode);
  if (!limits) return null;

  return wrapText(text, limits.maxChars, limits.maxLines);
}

/**
 * Wrap text for specific message type using its key (enum)
 * @param text - Text to wrap
 * @param messageTypeKey - Message type key (e.g., MessageTypeCode.FUND_HARD_CODED_FULL_PAGE)
 * @returns TextWrappingResult or null if message type not found
 * @example
 * const result = wrapTextForMessageTypeByKey(
 *   'Long text here',
 *   MessageTypeCode.FUND_HARD_CODED_FULL_PAGE
 * );
 */
export function wrapTextForMessageTypeByKey(
  text: string,
  messageTypeKey: MessageTypeCode,
): TextWrappingResult | null {
  const limits = getMessageLimitsByKey(messageTypeKey);
  if (!limits) return null;

  return wrapText(text, limits.maxChars, limits.maxLines);
}

/**
 * Validate if text fits within message type constraints
 * @param text - Text to validate
 * @param messageTypeCode - Message type code
 * @returns true if text is valid for the message type
 * @example
 * const isValid = validateTextForMessageType('Short text', '05');
 * // Returns: true if text fits in 115 chars x 5 lines
 */
export function validateTextForMessageType(text: string, messageTypeCode: string): boolean {
  const result = wrapTextForMessageType(text, messageTypeCode);
  return result?.isValid ?? false;
}

/**
 * Get validation errors for text in specific message type
 * @param text - Text to validate
 * @param messageTypeCode - Message type code
 * @returns Array of error messages
 * @example
 * const errors = getTextValidationErrors('Very long text...', '05');
 * // Returns: ['Text requires 8 lines but maximum allowed is 5 lines']
 */
export function getTextValidationErrors(text: string, messageTypeCode: string): string[] {
  const result = wrapTextForMessageType(text, messageTypeCode);
  return result?.errors ?? ['Invalid message type code'];
}

/**
 * Format wrapped text as single string with line breaks
 * @param wrappingResult - Result from wrapText function
 * @param lineBreak - Line break character(s) (default: '\n')
 * @returns Formatted text with line breaks
 * @example
 * const result = wrapTextForMessageType('Long text', '05');
 * const formatted = formatWrappedText(result);
 * // Returns: "Line 1\nLine 2\nLine 3"
 */
export function formatWrappedText(wrappingResult: TextWrappingResult, lineBreak = '\n'): string {
  return wrappingResult.lines.join(lineBreak);
}

/**
 * Get preview of how text will wrap for a message type
 * @param text - Text to preview
 * @param messageTypeCode - Message type code
 * @returns Object with formatted preview and validation info
 * @example
 * const preview = getTextWrappingPreview('Long text here', '05');
 * // Returns: { preview: "Line 1\nLine 2", isValid: true, errors: [], stats: {...} }
 */
export function getTextWrappingPreview(
  text: string,
  messageTypeCode: string,
): {
  preview: string;
  isValid: boolean;
  errors: string[];
  stats: TextWrappingResult['stats'];
} | null {
  const result = wrapTextForMessageType(text, messageTypeCode);
  if (!result) return null;

  return {
    preview: formatWrappedText(result),
    isValid: result.isValid,
    errors: result.errors,
    stats: result.stats,
  };
}

/**
 * Calculate remaining space for text in message type
 * @param currentText - Current text
 * @param messageTypeCode - Message type code
 * @returns Object with remaining lines and characters info
 * @example
 * const remaining = getRemainingSpace('Current text', '05');
 * // Returns: { remainingLines: 4, remainingCharsOnCurrentLine: 103, ... }
 */
export function getRemainingSpace(
  currentText: string,
  messageTypeCode: string,
): {
  remainingLines: number;
  remainingCharsOnCurrentLine: number;
  canAddMoreText: boolean;
  limits: MessageLimits;
} | null {
  const limits = getMessageLimits(messageTypeCode);
  if (!limits) return null;

  const result = wrapText(currentText, limits.maxChars, limits.maxLines);
  const lastLine = result.lines[result.lines.length - 1] || '';

  return {
    remainingLines: limits.maxLines - result.lines.length,
    remainingCharsOnCurrentLine: limits.maxChars - lastLine.length,
    canAddMoreText: result.lines.length < limits.maxLines,
    limits,
  };
}

// ============================================
// ADDITIONAL UTILITY FUNCTIONS
// ============================================

/**
 * Get message type options for dropdown
 * @returns Array of {value: code, label: description} objects
 */
export function getMessageTypeOptions(): { value: string; label: string }[] {
  return Object.values(MESSAGE_TYPES).map((type) => ({
    value: type.code,
    label: type.value,
  }));
}

/**
 * Get all message type codes as array
 * @returns Array of message type codes
 */
export function getMessageTypeCodes(): string[] {
  return Object.values(MESSAGE_TYPES).map((type) => type.code);
}

/**
 * Check if value is a valid message type code
 * @param code - Value to check
 * @returns true if valid message type code
 */
export function isValidMessageCode(code: string): boolean {
  return Object.values(MESSAGE_TYPES).some((type) => type.code === code);
}

/**
 * Check if message type requires fund fields (is fund-specific)
 * @param code - Message type code
 * @returns true if message type requires fund fields
 */
export function isFundSpecificMessage(code: string): boolean {
  const fundSpecificCode = MESSAGE_TYPES[MessageTypeCode.FUND_SPECIFIC_FULL_PAGE].code;
  return code === fundSpecificCode;
}
